import fr from 'facet-rest';
import React from 'react';
import PropTypes from 'prop-types';
import useComponentDidMount from 'Hooks/useComponentDidMount';

const FileOptionContext = React.createContext(undefined);

// Actions
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';

const SET_FILE_OPTION_COLLECTION = 'set-file-option-collection';

const LOADING = 'loading';

const LOADING_FAILED = 'loading-failed';

const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    isLoading: true,
    fileOptionCollection: null,
    loadingError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isLoading: false,
        loadingError: null,
        fileOptionCollection: action.fileOptionCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isLoading: false,
        loadingError: action.loadingError,
      };
    }
    case LOADING: {
      return {
        ...state,
        isLoading: true,
      };
    }
    case LOADING_FAILED: {
      return {
        ...state,
        isLoading: true,
        loadingError: action.error,
      };
    }
    case SET_FILE_OPTION_COLLECTION: {
      return {
        ...state,
        isLoading: false,
        fileOptionCollection: action.fileOptionCollection,
      };
    }
    default: {
      throw new Error(`Unhandled FileOptionContext action type: ${action.type}`);
    }
  }
};

const FileOptionProvider = ({
  fileOptionLink,
  children,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const refreshFileOptions = React.useCallback(async () => {
    const { fileOptionCollection } = state;

    try {
      const coll = await fileOptionCollection.link('self').get();

      dispatch({ type: SET_FILE_OPTION_COLLECTION, fileOptionCollection: coll });
    } catch (err) {
      dispatch({ type: LOADING_FAILED, error: err });
    }
  }, [state]);

  const setLoading = React.useCallback(() => {
    try {
      dispatch({ type: LOADING });
    } catch (err) {
      dispatch({ type: LOADING_FAILED, error: err });
    }
  }, [state]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      fileOptionCollection: state.fileOptionCollection,
      isLoading: state.isLoading,
      loadingError: state.loadingError,
      refreshFileOptions,
      setLoading,
    };
  }, [
    state,
    refreshFileOptions,
    setLoading,
  ]);

  useComponentDidMount(async () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });
      const options = await fr.http.get(fileOptionLink);

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        fileOptionCollection: options,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, loadingError: err });
    }
  });

  return (
    <FileOptionContext.Provider value={memoizedValue}>
      {children}
    </FileOptionContext.Provider>
  );
};

const useFileOptionContext = () => {
  const context = React.useContext(FileOptionContext);
  if (context === undefined) {
    throw new Error('useFileOptionContext must be used within a FileOptionProvider');
  }
  return context;
};

FileOptionProvider.propTypes = {
  fileOptionLink: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  FileOptionProvider,
  useFileOptionContext,
};
